import React, { useState, useEffect } from 'react';
import { getBrands, createBrand, updateBrand, deleteBrand } from '../../services/brandService';
import { uploadFile } from '../../services/upload';
import './Brands.css';

const PRODUCT_STATUS = {
  DELETED: 0,
  INACTIVE: 100,
  ACTIVE: 200,
};

const getStatusMeta = (value) => {
  const num = Number(value);
  if (num === PRODUCT_STATUS.ACTIVE) return { label: 'Active', tone: 'success' };
  if (num === PRODUCT_STATUS.INACTIVE) return { label: 'Inactive', tone: 'warning' };
  if (num === PRODUCT_STATUS.DELETED) return { label: 'Deleted', tone: 'muted' };
  return { label: 'Unknown', tone: 'muted' };
};

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [currentBrand, setCurrentBrand] = useState(null);
  const [activeTab, setActiveTab] = useState('uz');
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [formData, setFormData] = useState({
    name: { uz: '', ru: '', oz: '' },
    description: { uz: '', ru: '', oz: '' },
    logo: '',
  });

  useEffect(() => {
    fetchBrands();
  }, [page, limit, search]);

  const fetchBrands = async () => {
    try {
      const offset = (page - 1) * limit;
      const response = await getBrands({
        limit,
        offset,
        search
      });
      setBrands(response.data.brands || []);
      setTotal(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleInputChange = (field, lang, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadFile(file);
      setFormData(prev => ({ ...prev, logo: response.data.link }));
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentBrand) {
        await updateBrand({
          ...formData,
          guid: currentBrand.guid,
          status: 200 // Active status
        });
      } else {
        await createBrand(formData);
      }
      setModalOpen(false);
      fetchBrands();
    } catch (error) {
      console.error('Error saving brand:', error);
    }
  };

  const openModal = (brand = null) => {
    if (brand) {
      setCurrentBrand(brand);
      setFormData({
        name: brand.name,
        description: brand.description,
        logo: brand.logo,
      });
    } else {
      setCurrentBrand(null);
      setFormData({
        name: { uz: '', ru: '', oz: '' },
        description: { uz: '', ru: '', oz: '' },
        logo: '',
      });
    }
    setModalOpen(true);
  };

  const confirmDelete = (brand) => {
    setBrandToDelete(brand);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (brandToDelete) {
      try {
        await deleteBrand(brandToDelete.guid);
        fetchBrands();
        setDeleteModalOpen(false);
        setBrandToDelete(null);
      } catch (error) {
        console.error('Error deleting brand:', error);
      }
    }
  };

  return (
    <div className="brands-page">
      <div className="page-header">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search brands..."
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div className="header-actions">
          <button className="add-btn" onClick={() => openModal()}>
            + Add Brand
          </button>
        </div>
      </div>

      <div className="brands-table-container">
        <table className="brands-table">
          <thead>
            <tr>
              <th>№</th>
              <th>Logo</th>
              <th>Name (UZ)</th>
              <th>Description (UZ)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {brands.map((brand, index) => {
              const statusMeta = getStatusMeta(brand.status);
              return (
              <tr key={brand.guid}>
                <td>{(page - 1) * limit + index + 1}</td>
                <td>
                  <img src={brand.logo} alt={brand.name.uz} className="brand-logo" />
                </td>
                <td>{brand.name.uz}</td>
                <td>{brand.description.uz}</td>
                <td>
                  <span className={`status-badge ${statusMeta.tone}`}>{statusMeta.label}</span>
                </td>
                <td>
                  <button className="action-btn" onClick={() => openModal(brand)} aria-label="Edit brand">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button className="action-btn delete" onClick={() => confirmDelete(brand)} aria-label="Delete brand">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="pagination-info">
          Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button 
            disabled={page === 1} 
            onClick={() => handlePageChange(page - 1)}
            className="page-btn"
          >
            Previous
          </button>
          {[...Array(Math.ceil(total / limit))].map((_, i) => (
            <button
              key={i + 1}
              className={`page-btn ${page === i + 1 ? 'active' : ''}`}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button 
            disabled={page >= Math.ceil(total / limit)} 
            onClick={() => handlePageChange(page + 1)}
            className="page-btn"
          >
            Next
          </button>
        </div>
      </div>

      {deleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Delete Brand</h2>
              <button className="close-btn" onClick={() => setDeleteModalOpen(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)' }}>
                Are you sure you want to delete the brand <strong>{brandToDelete?.name?.uz}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteModalOpen(false)}>Cancel</button>
              <button className="btn-save" style={{ backgroundColor: 'var(--error)' }} onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{currentBrand ? 'Edit Brand' : 'Add Brand'}</h2>
              <button className="close-btn" onClick={() => setModalOpen(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Logo</label>
                  <div className="file-upload" onClick={() => document.getElementById('logo-input').click()}>
                    {formData.logo ? (
                      <img src={formData.logo} alt="Preview" className="preview-image" />
                    ) : (
                      <p>Click to upload logo</p>
                    )}
                    <input
                      id="logo-input"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                  </div>
                  {uploading && <p>Uploading...</p>}
                </div>

                <div className="lang-tabs">
                  {['uz', 'ru', 'oz'].map(lang => (
                    <button
                      key={lang}
                      type="button"
                      className={`lang-tab ${activeTab === lang ? 'active' : ''}`}
                      onClick={() => setActiveTab(lang)}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </div>

                <div className="form-group">
                  <label>Name ({activeTab.toUpperCase()})</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name[activeTab]}
                    onChange={(e) => handleInputChange('name', activeTab, e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description ({activeTab.toUpperCase()})</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.description[activeTab]}
                    onChange={(e) => handleInputChange('description', activeTab, e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={uploading}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Brands;
